/**
 * TasksListPage — custom Blueprint task-run list page.
 *
 * Built on the public `ScaffolderApi` because `ListTasksPage` is NOT exported
 * from `@backstage/plugin-scaffolder`. See PLAN.md → "Architecture: building
 * scaffolder sub-pages from public APIs" for the full backstory.
 *
 * ## Data flow
 *
 * - Ownership scope (My runs / All runs) is stored in the URL search param
 *   `?scope=mine|all` so links can deep-link into a specific scope.
 * - Status / template / date-range filters are local state (secondary filters).
 * - `listTasks` is called server-side with limit=20 and an offset derived from
 *   the current page. Status/template/date filters are applied client-side on
 *   the fetched batch (the API only supports ownership + pagination).
 * - Polling: re-fetches every 10 s while any task in the current batch is
 *   `open` or `processing`. Stops automatically when all are terminal or on
 *   unmount.
 * - `/create/tasks/:taskId` IS handled here. Backstage's `<FlatRoutes>`
 *   rewrites every route path to `${path}/*` (see FlatRoutes.esm.js), so the
 *   `/create/tasks` route also matches `/create/tasks/<taskId>` — it never
 *   falls through to ScaffolderPage's internal router. We therefore mount an
 *   internal `<Routes>` that renders the OOTB scaffolder `<TaskPage>` (with
 *   full step logs) for the `:taskId` segment and the list for the index.
 *
 * ## Type notes
 *
 * `ScaffolderTask` (from @backstage/plugin-scaffolder-common):
 *   - task.id — task id
 *   - task.spec.templateInfo?.entityRef — template ref string
 *   - task.spec.templateInfo?.entity?.metadata?.name — template display name
 *   - task.spec.user?.ref — creator entity ref
 *   - task.status — 'open' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'skipped'
 *   - task.createdAt — ISO 8601 string
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonIcon,
  Card,
  Cell,
  CellText,
  Container,
  Flex,
  Focusable,
  FullPage,
  Row,
  Select,
  Skeleton,
  Table,
  TablePagination,
  Text,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  TooltipTrigger,
  type ColumnConfig,
} from '@backstage/ui';
import { RiExternalLinkLine, RiRefreshLine } from '@remixicon/react';
import { useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { TaskPage } from '@backstage/plugin-scaffolder';
import type { ScaffolderTask } from '@backstage/plugin-scaffolder-common';
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';
import { BlueprintsHeader } from './BlueprintsHeader';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 10_000;

/** Statuses that indicate an active (non-terminal) task. */
const NON_TERMINAL_STATUSES = new Set<string>(['open', 'processing']);

/** All possible task status values (kept in sync with TaskStatus from the API). */
const ALL_STATUSES = [
  'open',
  'processing',
  'completed',
  'failed',
  'cancelled',
  'skipped',
] as const;

type DateRange = '24h' | '7d' | '30d' | 'all';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Human-friendly relative timestamp. No external dependency — simple inline
 * implementation; rounds to the nearest largest unit.
 */
export function formatRelativeTime(isoDate: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  if (diffMs < 0) return 'just now';
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Returns a display name for the template referenced by the task. Falls back
 * gracefully when templateInfo is absent.
 */
function templateDisplayName(task: ScaffolderTask): string {
  return (
    task.spec.templateInfo?.entity?.metadata?.name ??
    task.spec.templateInfo?.entityRef?.split('/').pop() ??
    task.spec.templateInfo?.entityRef ??
    '(unknown template)'
  );
}

/**
 * Status chips filled in their status colour, as on the original page, using
 * BUI's solid status token sets; cancelled and skipped stay neutral.
 */
const STATUS_STYLES: Record<
  string,
  { label: string; bg?: string; fg?: string }
> = {
  open: {
    label: 'Open',
    bg: 'var(--bui-announcement-bg)',
    fg: 'var(--bui-announcement-fg)',
  },
  processing: {
    label: 'Processing',
    bg: 'var(--bui-warning-bg)',
    fg: 'var(--bui-warning-fg)',
  },
  completed: {
    label: 'Completed',
    bg: 'var(--bui-positive-bg)',
    fg: 'var(--bui-positive-fg)',
  },
  failed: {
    label: 'Failed',
    bg: 'var(--bui-negative-bg)',
    fg: 'var(--bui-negative-fg)',
  },
  cancelled: { label: 'Cancelled' },
  skipped: { label: 'Skipped' },
};

function StatusChip({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? { label: status };
  return (
    <Badge
      style={
        s.bg
          ? { backgroundColor: s.bg, border: `1px solid ${s.bg}` }
          : { border: '1px solid var(--bui-border-2)' }
      }
    >
      <Text
        variant="body-small"
        weight="bold"
        color={s.fg ? undefined : 'secondary'}
        style={{
          // Trim the line box to the capitals so the text sits in the middle.
          textBox: 'trim-both cap alphabetic',
          ...(s.fg ? { color: s.fg } : {}),
        }}
      >
        {s.label}
      </Text>
    </Badge>
  );
}

/**
 * Compute a cutoff timestamp (ms since epoch) for the selected date range.
 * Returns 0 for 'all' (no cutoff).
 */
function dateCutoffMs(range: DateRange): number {
  const rangeMs: Record<Exclude<DateRange, 'all'>, number> = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
  };
  if (range === 'all') return 0;
  return Date.now() - rangeMs[range];
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * TasksListPage — internal router for the Blueprints "Task Runs" tab.
 *
 * `<FlatRoutes>` mounts this component at `/create/tasks/*`, so we own both the
 * list (index) and the per-task detail (`:taskId`) routes. The detail route
 * renders the OOTB scaffolder `<TaskPage>`, which provides the step-by-step run
 * logs that the custom list view cannot show.
 */
export default function TasksListPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<TaskRunsList />} />
      <Route path=":taskId" element={<TaskPage />} />
    </Routes>
  );
}

/**
 * TaskRunsList — server-paginated task run list with My/All toggle, status
 * filter, template autocomplete, date range filter, auto-refresh, and retry.
 *
 * Rendered at the index of `/create/tasks`. Per-task detail
 * (`/create/tasks/:taskId`) is handled by the sibling `<TaskPage>` route above.
 */
function TaskRunsList(): JSX.Element {
  const scaffolderApi = useApi(scaffolderApiRef);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Scope (URL-persisted) ──────────────────────────────────────────────────
  //
  // ?scope=all  → All runs (filterByOwnership: 'all')
  // ?scope=mine (or absent) → My runs (filterByOwnership: 'owned')
  const scope: 'mine' | 'all' =
    searchParams.get('scope') === 'all' ? 'all' : 'mine';
  const filterByOwnership: 'owned' | 'all' = scope === 'all' ? 'all' : 'owned';

  // ── Client-side filters (local state) ──────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [templateFilter, setTemplateFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>('all');

  // ── Pagination ────────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);

  // ── Fetch trigger (incremented to force a re-fetch, e.g. after retry) ─────
  const [refetchKey, setRefetchKey] = useState(0);

  // ── Fetched data ──────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState<ScaffolderTask[]>([]);
  const [totalTasks, setTotalTasks] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  // ── Per-row retry state ───────────────────────────────────────────────────
  const [retrying, setRetrying] = useState<string | null>(null);

  // ── Fetch + polling effect ────────────────────────────────────────────────
  //
  // A single `cancelled` flag prevents setState calls after unmount. A timeout
  // id is captured in closure and cleared both on unmount and when any
  // dependency changes (the effect re-runs).
  useEffect(() => {
    let cancelled = false;
    let timerId: ReturnType<typeof setTimeout> | null = null;

    async function fetchTasks() {
      setLoading(true);
      setError(undefined);

      try {
        const result = await scaffolderApi.listTasks({
          filterByOwnership,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        });

        if (cancelled) return;

        setTasks(result.tasks);
        setTotalTasks(result.totalTasks);
        setLoading(false);

        // Poll again in 10 s if any task is still active.
        // Polling stops automatically when all tasks are terminal.
        const hasActive = result.tasks.some(t =>
          NON_TERMINAL_STATUSES.has(t.status),
        );
        if (hasActive) {
          timerId = setTimeout(fetchTasks, POLL_INTERVAL_MS);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        }
      }
    }

    fetchTasks();

    // Cleanup: prevent state updates from stale async operations and clear
    // any pending poll timer.
    return () => {
      cancelled = true;
      if (timerId !== null) clearTimeout(timerId);
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filterByOwnership, page, refetchKey]);
  // Note: scaffolderApi is excluded from deps — it is a stable singleton
  // reference from the ApiRef registry and will not change during the
  // component's lifetime.

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleScopeChange = useCallback(
    (value: 'mine' | 'all') => {
      // Use the functional form so any other existing query params are preserved.
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('scope', value);
        return next;
      });
      setPage(0); // reset to first page when switching scope
    },
    [setSearchParams],
  );

  const handleRetry = useCallback(
    async (taskId: string) => {
      setRetrying(taskId);
      try {
        await scaffolderApi.retry(taskId);
        // Trigger a fresh fetch so the retried task's new status is visible.
        setRefetchKey(k => k + 1);
      } catch {
        // Silently swallow the error for now — the user can see the task
        // still shows "failed" and click retry again.
        // TODO (follow-up): surface a snackbar/error inline.
      } finally {
        setRetrying(null);
      }
    },
    [scaffolderApi],
  );

  // ── Client-side filtering ─────────────────────────────────────────────────
  //
  // NOTE: these filters run against the currently fetched page only. The
  // scaffolder API supports only ownership + pagination server-side, so status,
  // template, and date-range filters cannot be pushed to the server. If the
  // current page yields no matches, try loading another page or widening the
  // date range.

  const filteredTasks = useMemo(() => {
    const cutoff = dateCutoffMs(dateRange);
    return tasks.filter(task => {
      // Status filter
      if (statusFilter.length > 0 && !statusFilter.includes(task.status)) {
        return false;
      }
      // Template filter (match on entityRef substring)
      if (
        templateFilter !== null &&
        !(task.spec.templateInfo?.entityRef ?? '').includes(templateFilter)
      ) {
        return false;
      }
      // Date range filter
      if (cutoff > 0 && new Date(task.createdAt).getTime() < cutoff) {
        return false;
      }
      return true;
    });
  }, [tasks, statusFilter, templateFilter, dateRange]);

  // ── Template options for autocomplete ─────────────────────────────────────
  // Unique entityRefs from current task batch, sorted alphabetically.
  const templateOptions = useMemo(
    () =>
      Array.from(
        new Set(
          tasks
            .map(t => t.spec.templateInfo?.entityRef)
            .filter((r): r is string => typeof r === 'string'),
        ),
      ).sort(),
    [tasks],
  );

  // ── Empty-state detection ─────────────────────────────────────────────────
  const isServerEmpty = !loading && tasks.length === 0 && !error;
  const isFilterEmpty =
    !loading && filteredTasks.length === 0 && tasks.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────

  const templateLabel = (ref: string) => {
    // Show the last part of the entityRef as a friendly name
    const match = tasks.find(t => t.spec.templateInfo?.entityRef === ref);
    return (
      match?.spec.templateInfo?.entity?.metadata?.name ??
      ref.split('/').pop() ??
      ref
    );
  };

  const columns: ColumnConfig<ScaffolderTask>[] = [
    {
      id: 'template',
      label: 'Template',
      isRowHeader: true,
      cell: task => <CellText title={templateDisplayName(task)} />,
    },
    {
      id: 'status',
      label: 'Status',
      cell: task => (
        <Cell>
          <StatusChip status={task.status} />
        </Cell>
      ),
    },
    {
      id: 'created',
      label: 'Created',
      cell: task => <CellText title={formatRelativeTime(task.createdAt)} />,
    },
    {
      id: 'creator',
      label: 'Created by',
      cell: task => <CreatorCell task={task} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      cell: task => (
        <TaskActions
          task={task}
          onOpen={() => navigate(`/create/tasks/${task.id}`)}
          onRetry={() => handleRetry(task.id)}
          isRetrying={retrying === task.id}
        />
      ),
    },
  ];

  return (
    <FullPage>
      <BlueprintsHeader
        subtitle="Track the status of your Blueprint runs"
        pageTitleOverride="Blueprints – Task Runs"
      />
      <Container py="6">
        {/* Scope toggle — My runs / All runs */}
        <Flex mb="4">
          <ToggleButtonGroup
            aria-label="Task run scope"
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={[scope]}
            onSelectionChange={keys => {
              const [next] = Array.from(keys);
              if (next === 'mine' || next === 'all') handleScopeChange(next);
            }}
          >
            <ToggleButton id="mine">My runs</ToggleButton>
            <ToggleButton id="all">All runs</ToggleButton>
          </ToggleButtonGroup>
        </Flex>

        {/* Secondary filters */}
        <Flex align="end" gap="4" mb="4" style={{ flexWrap: 'wrap' }}>
          <Box style={{ width: 200 }}>
            <Select
              label="Status"
              selectionMode="multiple"
              placeholder="Any status"
              options={ALL_STATUSES.map(s => ({
                value: s,
                label: STATUS_STYLES[s]?.label ?? s,
              }))}
              value={statusFilter}
              onChange={keys =>
                setStatusFilter(Array.isArray(keys) ? keys.map(String) : [])
              }
            />
          </Box>

          <Box style={{ width: 260 }}>
            <Select
              label="Template"
              search
              placeholder="Any template"
              options={[
                { value: '', label: 'Any template' },
                ...templateOptions.map(ref => ({
                  value: ref,
                  label: templateLabel(ref),
                })),
              ]}
              value={templateFilter ?? ''}
              onChange={key => setTemplateFilter(key ? String(key) : null)}
            />
          </Box>

          <Box style={{ width: 200 }}>
            <Select
              label="Created"
              options={[
                { value: 'all', label: 'Any time' },
                { value: '24h', label: 'Last 24 hours' },
                { value: '7d', label: 'Last 7 days' },
                { value: '30d', label: 'Last 30 days' },
              ]}
              value={dateRange}
              onChange={key => setDateRange(String(key) as DateRange)}
            />
          </Box>

          {/* Clear filters shortcut */}
          {(statusFilter.length > 0 ||
            templateFilter !== null ||
            dateRange !== 'all') && (
            <Button
              variant="tertiary"
              onPress={() => {
                setStatusFilter([]);
                setTemplateFilter(null);
                setDateRange('all');
              }}
            >
              Clear filters
            </Button>
          )}
        </Flex>

        {/* Error */}
        {error && (
          <Alert
            role="alert"
            status="danger"
            icon
            title="Unable to load task runs"
            description={error.message}
          />
        )}

        {/* Loading indicator (initial load only — not during polls) */}
        {loading && tasks.length === 0 && (
          <Box role="progressbar" aria-label="Loading task runs">
            <Skeleton width="100%" height={4} rounded />
          </Box>
        )}

        {/* Empty state — no tasks at all for this scope */}
        {isServerEmpty && !error && (
          <EmptyState title="No task runs yet">
            {scope === 'mine'
              ? "You haven't run any Blueprints yet. Choose a template to get started."
              : 'No Blueprint runs found in this Backstage instance.'}
          </EmptyState>
        )}

        {/* Empty state — filters exclude all results */}
        {isFilterEmpty && (
          <EmptyState title="No task runs match your filters">
            Filters apply to the currently loaded page only. Try clearing
            filters, navigating to another page, or widening the date range.
          </EmptyState>
        )}

        {/* Table */}
        {filteredTasks.length > 0 && (
          // On a card, as the original's Paper, with the table to its edges.
          <Card style={{ padding: 0 }}>
            <Table
              aria-label="Task runs"
              columnConfig={columns}
              data={filteredTasks}
              pagination={{ type: 'none' }}
              rowConfig={({ item }) => (
                // The whole row opens the run; its buttons keep their own
                // actions.
                <Row
                  id={item.id}
                  columns={columns}
                  href={`/create/tasks/${item.id}`}
                >
                  {column => column.cell(item)}
                </Row>
              )}
            />

            {/* Server pagination; without a total it pages by batch size. */}
            <Box px="3" py="2">
              <TablePagination
                pageSize={PAGE_SIZE}
                pageSizeOptions={[]}
                offset={page * PAGE_SIZE}
                totalCount={totalTasks}
                hasPreviousPage={page > 0}
                hasNextPage={
                  totalTasks !== undefined
                    ? (page + 1) * PAGE_SIZE < totalTasks
                    : !loading && tasks.length >= PAGE_SIZE
                }
                onPreviousPage={() => setPage(p => Math.max(0, p - 1))}
                onNextPage={() => setPage(p => p + 1)}
              />
            </Box>
          </Card>
        )}
      </Container>
    </FullPage>
  );
}

/** Centred empty-state copy, as the original page. */
function EmptyState({ title, children }: { title: string; children: string }) {
  return (
    <Flex direction="column" align="center" gap="2" py="8">
      <Text as="h2" variant="title-x-small" color="secondary">
        {title}
      </Text>
      <Text as="p" color="secondary" style={{ textAlign: 'center' }}>
        {children}
      </Text>
    </Flex>
  );
}

// ── Row cells ─────────────────────────────────────────────────────────────────

function CreatorCell({ task }: { task: ScaffolderTask }) {
  const creatorRef = task.spec.user?.ref;
  const creatorLabel = creatorRef
    ? (creatorRef.split('/').pop() ?? creatorRef)
    : '—';
  return (
    <Cell>
      <TooltipTrigger delay={300}>
        <Focusable>
          <Text as="span">{creatorLabel}</Text>
        </Focusable>
        <Tooltip>{creatorRef ?? 'Unknown'}</Tooltip>
      </TooltipTrigger>
    </Cell>
  );
}

interface TaskActionsProps {
  task: ScaffolderTask;
  onOpen: () => void;
  onRetry: () => void;
  isRetrying: boolean;
}

function TaskActions({ task, onOpen, onRetry, isRetrying }: TaskActionsProps) {
  return (
    <Cell>
      <Flex align="center" gap="1">
        {/* Open task */}
        <TooltipTrigger delay={300}>
          <ButtonIcon
            size="small"
            variant="tertiary"
            aria-label="Open task"
            icon={<RiExternalLinkLine size={16} />}
            onPress={onOpen}
          />
          <Tooltip>Open task</Tooltip>
        </TooltipTrigger>

        {/* Retry — only for failed tasks */}
        {task.status === 'failed' && (
          <TooltipTrigger delay={300}>
            <ButtonIcon
              size="small"
              variant="tertiary"
              aria-label="Retry task"
              icon={<RiRefreshLine size={16} />}
              isDisabled={isRetrying}
              onPress={onRetry}
            />
            <Tooltip>Retry task</Tooltip>
          </TooltipTrigger>
        )}
      </Flex>
    </Cell>
  );
}
